/**
 * ImageUpload - Reusable controlled component for JPEG/PNG image uploads
 *
 * Features:
 * - Client-side Canvas resize to target dimensions
 * - Iterative JPEG quality reduction if output exceeds maxFileSize
 * - Dashed border drop zone → preview with Remove button
 * - Follows existing SVG upload pattern from BrandingSettingsTab
 */
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { resizeImage } from '@/utils/imageResize';

export interface ImageUploadProps {
  /** Raw base64 string (no data URI prefix), or null */
  value: string | null;
  /** Called with raw base64 string or null (on remove) */
  onChange: (value: string | null) => void;
  /** Label shown above the upload area */
  label: string;
  /** Helper text below the label */
  description?: string;
  /** Max width for resize (proportional) */
  targetMaxWidth: number;
  /** Max height for resize (proportional, optional) */
  targetMaxHeight?: number;
  /** Max file size in bytes after resize */
  maxFileSize: number;
  /** Output format */
  outputFormat: 'jpeg' | 'png';
  /** JPEG quality (0-1), default 0.85 */
  quality?: number;
  /** data-testid for testing */
  testId?: string;
}

export function ImageUpload({
  value,
  onChange,
  label,
  description,
  targetMaxWidth,
  targetMaxHeight,
  maxFileSize,
  outputFormat,
  quality = 0.85,
  testId,
}: ImageUploadProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const inputId = testId ? `${testId}-input` : `image-upload-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const mimeType = outputFormat === 'jpeg' ? 'image/jpeg' : 'image/png';

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validate file type
    if (!file.type.startsWith('image/') || !['image/jpeg', 'image/png'].includes(file.type)) {
      setUploadError('Please upload a JPEG or PNG image');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await resizeImage(
        file,
        targetMaxWidth,
        targetMaxHeight,
        outputFormat,
        quality,
        maxFileSize
      );

      if (result.sizeBytes > maxFileSize) {
        setUploadError(
          `Image still too large after resize: ${(result.sizeBytes / 1024).toFixed(0)}KB (max ${(maxFileSize / 1024).toFixed(0)}KB). Try a smaller image.`
        );
        return;
      }

      onChange(result.base64);
    } catch (error) {
      setUploadError('Failed to process image');
      console.error('Image upload error:', error);
    } finally {
      setIsProcessing(false);
      // Reset file input so same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div data-testid={testId}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      {description && (
        <p className="text-xs text-gray-500 mb-3">{description}</p>
      )}

      {/* Upload Area or Preview */}
      {!value ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileSelect}
            className="hidden"
            id={inputId}
            data-testid={testId ? `${testId}-file-input` : undefined}
          />
          <label
            htmlFor={inputId}
            className="cursor-pointer flex flex-col items-center gap-2"
          >
            <Upload className="w-10 h-10 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              {isProcessing ? 'Processing...' : `Click to upload ${outputFormat.toUpperCase()} image`}
            </span>
            <span className="text-xs text-gray-500">
              Max {(maxFileSize / 1024).toFixed(0)}KB, resized to {targetMaxWidth}px wide
            </span>
          </label>
        </div>
      ) : (
        /* Image Preview */
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{label} Preview</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={testId ? `${testId}-remove` : undefined}
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>

          <div className="bg-white p-4 rounded border border-gray-200 flex items-center justify-center min-h-[120px]">
            <img
              src={`data:${mimeType};base64,${value}`}
              alt={`${label} preview`}
              className="max-w-full max-h-48 object-contain"
              data-testid={testId ? `${testId}-preview` : undefined}
            />
          </div>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700" data-testid={testId ? `${testId}-error` : undefined}>
            {uploadError}
          </p>
        </div>
      )}
    </div>
  );
}
